import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { AlertTriangle, Check, Copy, CreditCard, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { cn } from '@repo/ui/src/lib/utils';
import { type Invoice, hasNothingToCollect } from '@repo/shared-types';
import {
  useDraftInvoiceMessage,
  useDraftReminder,
  useInvoiceQr,
  useMarkShared,
  useSendInvoice,
  useSendReminder,
} from '../../../hooks/useInvoices';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { DEFAULT_INVOICE_MESSAGE_TEMPLATE, buildInvoiceMessageData, resolveTemplate } from '../../../lib/invoiceMessage';
import { displayUrl } from '../../../lib/displayUrl';
import { QrActions } from './QrActions';
import { useAuthStore } from '../../../stores/authStore';

type Mode = 'send' | 'reminder';
// "manual" = the default "Gửi tay" tab (message + public link + QR); "auto" =
// the not-yet-purchasable "Zalo tự động" tab (coming soon, see THI-74);
// "email" is the pre-existing AI-drafted email flow, unchanged.
type Tab = 'manual' | 'auto' | 'email';

interface SendInvoiceDialogProps {
  invoice: Invoice;
  mode: Mode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WEB_URL = import.meta.env.VITE_WEB_URL || 'http://localhost:5173';

export function SendInvoiceDialog({
  invoice,
  mode,
  open,
  onOpenChange,
}: SendInvoiceDialogProps) {
  const { t } = useTranslation('invoices');
  const navigate = useNavigate();

  const hasEmail = !!invoice.client?.email;
  // A manual deduction line can bring totalAmount to exactly zero — nothing
  // is left to collect, so a QR to scan or a paid ZNS send announcing "0đ"
  // makes no sense. Shared predicate (see its doc comment) so this can't
  // drift from the API guards on GET /:id/qr and POST /:id/send-zalo, which
  // are the actual backstop — this is only the UI-side "don't even offer it".
  const nothingToCollect = hasNothingToCollect(invoice);

  const [tab, setTab] = useState<Tab>('manual');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [emailPopulated, setEmailPopulated] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState(false);
  const emailDraftRequested = useRef(false);

  // C1 fix: opening the public link permanently stamps the invoice's
  // publicViewedAt and logs an INVOICE_VIEWED client-activity row — that route
  // is public/unauthenticated by design, so no server guard reaches it. Hide
  // the copy-link field (with a reason) while impersonating; see the matching
  // note in InvoiceDetailPage.tsx for why this one link is allowed to know.
  // The resolved message text embeds the same link, so it is hidden for the
  // same reason — otherwise the guard below would be trivially bypassed by
  // reading (and copying) it out of the message box instead.
  const impersonatedBy = useAuthStore((s) => s.impersonatedBy);

  const draftInvoice = useDraftInvoiceMessage(invoice.id);
  const draftReminder = useDraftReminder(invoice.id);
  const sendInvoice = useSendInvoice(invoice.id);
  const sendReminder = useSendReminder(invoice.id);
  const markShared = useMarkShared(invoice.id);

  const draftMutation = mode === 'send' ? draftInvoice : draftReminder;
  const sendMutation = mode === 'send' ? sendInvoice : sendReminder;

  // Reset all per-open state. Default tab is "Gửi tay" (manual) — Zalo
  // auto-send is not yet purchasable (THI-74) and email is opt-in, not the
  // forced first choice. The AI email draft is fetched lazily (see effect
  // below) so opening the dialog to grab the message/QR doesn't trigger a
  // needless AI compose call.
  useEffect(() => {
    if (!open) {
      setTab('manual');
      setTo('');
      setSubject('');
      setBody('');
      setEmailPopulated(false);
      setCopiedLink(false);
      setCopiedMessage(false);
      emailDraftRequested.current = false;
      return;
    }
    setTab('manual');
  }, [open]);

  // Lazily draft the email only once the Email tab is actually selected.
  useEffect(() => {
    if (!open || tab !== 'email' || emailDraftRequested.current) return;
    emailDraftRequested.current = true;
    draftMutation.mutate(undefined, {
      onSuccess: (data) => {
        setTo(data.to ?? invoice.client?.email ?? '');
        setSubject(data.subject);
        setBody(data.body);
        setEmailPopulated(true);
      },
      onError: (err: Error & { code?: string }) => {
        setTo(invoice.client?.email ?? '');
        setSubject('');
        setBody('');
        setEmailPopulated(true);
        // THI-176: draft-message/draft-reminder now burn an ai_credit and can
        // 402 at the plan's monthly cap — surface the server's own message
        // (same as chat's ai_credit block) instead of the generic "draft
        // failed" toast, which would read as a bug and invite a pointless
        // retry. Minimal conformance only — a proper upsell banner is THI-178.
        if (err.code === 'TIER_LIMIT_REACHED') {
          toast.warning(err.message);
        } else {
          toast.warning(t('sendDialog.draftFailed'));
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  // Same QR source the chat ArtifactPanel uses (GET /invoices/:id/qr), keyed by
  // invoice id so it's shared/cached across the app (see useInvoiceQr's doc
  // comment). Only the manual tab renders a QR — the "Zalo tự động" tab is a
  // static coming-soon panel and must not make any network call.
  const {
    data: qrData,
    error: qrErrorRaw,
    refetch: refetchQr,
    isFetching: qrFetching,
  } = useInvoiceQr(invoice.id, open && tab === 'manual' && !nothingToCollect);
  const qrErrorCode = (qrErrorRaw as (Error & { code?: string }) | null)?.code ?? null;

  const publicLink = `${WEB_URL}/invoice/p/${invoice.publicToken}`;

  // The manual-send message: org-editable template (falls back to the
  // built-in default) resolved for this invoice. Pure/deterministic — see
  // apps/dashboard/src/lib/invoiceMessage.ts.
  const { data: orgSettings } = useOrgSettings();
  const template = orgSettings?.invoiceMessageTemplate ?? DEFAULT_INVOICE_MESSAGE_TEMPLATE;
  const resolvedMessage = useMemo(
    () => resolveTemplate(template, buildInvoiceMessageData(invoice, publicLink)),
    [template, invoice, publicLink],
  );

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopiedLink(true);
      toast.success(t('sendDialog.zalo.copied'));
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      toast.error(t('sendDialog.zalo.copyFailed'));
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(resolvedMessage);
      setCopiedMessage(true);
      toast.success(t('sendDialog.manual.messageCopied'));
      setTimeout(() => setCopiedMessage(false), 2000);
    } catch {
      toast.error(t('sendDialog.manual.messageCopyFailed'));
    }
  };

  const goToTemplateSettings = () => {
    // Quy tắc học phí (billing-rules) is where the template card lives; the
    // hash scrolls straight to it via TEMPLATE_CARD_ANCHOR_ID.
    onOpenChange(false);
    navigate('/settings/billing-rules#invoice-message-template');
  };

  // Manual/QR "primary action" only exists for the initial send (it flips the
  // invoice DRAFT -> PENDING). A reminder dialog opens on an invoice that's
  // already PENDING/PARTIAL, so mark-shared would 400 (BAD_STATUS) — for a
  // reminder, the manual tab is purely informational (copy & resend).
  const canMarkShared = mode === 'send';

  const handleMarkShared = () => {
    if (markShared.isPending) return;
    markShared.mutate(
      { channel: 'zalo' },
      {
        onSuccess: () => {
          toast.success(t('sendDialog.zalo.markedToast'));
          onOpenChange(false);
        },
        onError: () => {
          toast.error(t('sendDialog.toast.error'));
        },
      },
    );
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!to || !subject || !body || sendMutation.isPending) return;
    sendMutation.mutate(
      { to, subject, body },
      {
        onSuccess: () => {
          toast.success(
            mode === 'send'
              ? t('sendDialog.toast.sent')
              : t('sendDialog.toast.reminded'),
          );
          onOpenChange(false);
        },
        onError: () => {
          toast.error(t('sendDialog.toast.error'));
        },
      },
    );
  };

  const isBusy = sendMutation.isPending || markShared.isPending;

  const handleOpenChange = (next: boolean) => {
    if (!next && isBusy) return;
    onOpenChange(next);
  };

  const isDrafting = draftMutation.isPending;
  const canSubmitEmail =
    emailPopulated && !!to && !!subject && !!body && !sendMutation.isPending && !isDrafting;

  const qrPanel = (
    <div className="space-y-2">
      {nothingToCollect ? (
        <div className="flex items-start gap-2 px-3 py-3 bg-surface-50 border-l-2 border-surface-300 rounded">
          <Check className="w-4 h-4 text-surface-400 shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-surface-600">{t('sendDialog.zalo.nothingToCollect')}</p>
        </div>
      ) : qrData?.qrUrl ? (
        <div className="text-center">
          <img
            src={qrData.qrUrl}
            alt="VietQR"
            className="w-full max-w-[220px] mx-auto rounded border"
          />
          {qrData.paymentRef && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {t('sendDialog.zalo.paymentRef')}{' '}
              <span className="font-mono font-semibold">{qrData.transferContent}</span>
            </p>
          )}
          <QrActions qrUrl={qrData.qrUrl} paymentRef={qrData.paymentRef ?? null} />
        </div>
      ) : qrFetching ? (
        <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('sendDialog.qrError.loading')}
        </div>
      ) : qrErrorCode ? (
        <div className="flex items-start gap-2 px-3 py-3 bg-amber-50 border-l-2 border-amber-400 rounded">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[12.5px] font-medium text-amber-800">
              {qrErrorCode === 'BANK_NOT_LINKED'
                ? t('sendDialog.qrError.bankNotLinked')
                : t('sendDialog.qrError.unsupportedBank')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {qrErrorCode === 'BANK_NOT_LINKED' && (
                <Button
                  type="button"
                  size="sm"
                  className="h-7 gap-1.5 text-[12px]"
                  onClick={() => navigate('/settings/bank')}
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  {t('sendDialog.qrError.linkBank')}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-[12px]"
                disabled={qrFetching}
                onClick={() => refetchQr()}
              >
                {t('sendDialog.qrError.retry')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  const hasFooterAction = tab === 'email' || (tab === 'manual' && canMarkShared);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`sendDialog.title.${mode}`)}</DialogTitle>
          <DialogDescription>{t(`sendDialog.subtitle.${mode}`)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="inline-flex rounded-md border border-border p-0.5 gap-0.5">
            {(
              [
                ['manual', t('sendDialog.tabs.manual')],
                ['auto', t('sendDialog.tabs.auto')],
                ['email', t('sendDialog.tabs.email')],
              ] as const
            ).map(([value, label]) => {
              const disabled = value === 'email' && !hasEmail;
              return (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={tab === value ? 'default' : 'ghost'}
                  disabled={disabled}
                  title={disabled ? t('sendDialog.tabs.emailDisabledHint') : undefined}
                  onClick={() => setTab(value)}
                  className={cn('h-7 text-[12.5px] px-3', disabled && 'opacity-50')}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          {tab === 'email' && !hasEmail && (
            <p className="text-[12px] text-muted-foreground">
              {t('sendDialog.tabs.emailDisabledHint')}
            </p>
          )}

          {tab === 'manual' && (
            <div className="space-y-4">
              {impersonatedBy ? (
                <p className="text-[12px] text-muted-foreground">
                  {t('detail.publicLinkHiddenImpersonating')}
                </p>
              ) : (
                <>
                  {/* The template is a payment request ("Tổng cộng là...",
                      "quét mã QR thanh toán...") — an invoice with nothing
                      left to collect has nothing for it to say, and the QR
                      panel below already carries its own nothing-to-collect
                      note. Showing both would let a teacher copy a message
                      that contradicts the note two inches below it. */}
                  {!nothingToCollect && (
                    <div className="space-y-2">
                      <Label htmlFor="resolved-message">{t('sendDialog.manual.messageLabel')}</Label>
                      <Textarea
                        id="resolved-message"
                        readOnly
                        rows={8}
                        value={resolvedMessage}
                        className="resize-none text-[13px] leading-relaxed"
                      />
                      <Button type="button" onClick={handleCopyMessage} className="w-full gap-2">
                        {copiedMessage ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {t('sendDialog.manual.copyMessage')}
                      </Button>
                      <p className="text-[12px] text-muted-foreground">
                        {t('sendDialog.manual.editTemplatePrefix')}{' '}
                        <button
                          type="button"
                          onClick={goToTemplateSettings}
                          className="underline underline-offset-2 hover:text-foreground"
                        >
                          {t('sendDialog.manual.editTemplateLinkLabel')}
                        </button>
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="public-link">{t('sendDialog.zalo.linkLabel')}</Label>
                    <div className="flex gap-2">
                      {/* Display only (THI-192 §12.2) — `handleCopyLink` and
                          `resolvedMessage` both still carry `publicLink` in
                          full. This one Input serves BOTH modes: `mode` only
                          decides `canMarkShared`, so the reminder dialog gets
                          the same trimmed display from this single edit. */}
                      <Input id="public-link" value={displayUrl(publicLink)} readOnly className="font-mono text-[12.5px]" />
                      <Button type="button" variant="outline" size="icon" onClick={handleCopyLink}>
                        {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>{t('sendDialog.zalo.qrLabel')}</Label>
                {qrPanel}
              </div>

              {!canMarkShared && (
                <p className="text-[12px] text-muted-foreground">
                  {t('sendDialog.zalo.reminderHint')}
                </p>
              )}
            </div>
          )}

          {tab === 'auto' && (
            <div className="rounded-md border border-dashed border-surface-200 bg-surface-50 px-4 py-8 text-center">
              <p className="text-[13px] text-surface-600">{t('sendDialog.auto.comingSoon')}</p>
            </div>
          )}

          {tab === 'email' &&
            (isDrafting && !emailPopulated ? (
              <div className="py-8 flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
                <p className="text-sm">{t('sendDialog.drafting')}</p>
              </div>
            ) : (
              <form
                id="send-invoice-email-form"
                onSubmit={handleEmailSubmit}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="send-to">{t('sendDialog.field.to')}</Label>
                  <Input
                    id="send-to"
                    type="email"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="send-subject">{t('sendDialog.field.subject')}</Label>
                  <Input
                    id="send-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="send-body">{t('sendDialog.field.body')}</Label>
                  <Textarea
                    id="send-body"
                    rows={10}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    required
                  />
                </div>
              </form>
            ))}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isBusy}>
            {hasFooterAction ? t('sendDialog.cancel') : t('sendDialog.close')}
          </Button>
          {tab === 'email' ? (
            <Button type="submit" form="send-invoice-email-form" disabled={!canSubmitEmail}>
              {sendMutation.isPending ? t('sendDialog.sending') : t('sendDialog.send')}
            </Button>
          ) : tab === 'manual' && canMarkShared ? (
            <Button type="button" onClick={handleMarkShared} disabled={markShared.isPending}>
              {markShared.isPending
                ? t('sendDialog.sending')
                : t('sendDialog.zalo.markSharedAction')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
